import { apiGet, apiPost, apiPatch, apiDelete } from "./client";
import type {
  Opportunity,
  OpportunityInput,
  DealIntel,
} from "@sales-ops-amplify/shared";

export interface ListOpportunitiesResponse {
  opportunities: Opportunity[];
  total: number;
  limit?: number;
  offset?: number;
  hasMore?: boolean;
}

export interface ListOpportunitiesParams {
  company?: string;
  search?: string;
  q?: string;
  status?: string;
  stage?: string;
  stageId?: string;
  source?: string;
  pipelineId?: string;
  pipelineConfigId?: string;
  ownerUserId?: string;
  closeDateFrom?: string;
  closeDateTo?: string;
  includeArchived?: "true" | "false";
  sort?: "amount" | "closeDate" | "updatedAt";
  /** "true" | "false" — filter by presence of Zero pipeline */
  piped?: "true" | "false";
  limit?: number;
  offset?: number;
}

export async function listOpportunities(
  params?: ListOpportunitiesParams,
): Promise<ListOpportunitiesResponse> {
  const res = await apiGet<ListOpportunitiesResponse>(
    "/api/opportunities",
    params as Record<string, string | number | boolean | undefined>,
  );
  const items = res.opportunities ?? [];
  return { ...res, opportunities: items, total: res.total ?? items.length };
}

export async function getOpportunity(opportunityId: string): Promise<Opportunity> {
  return apiGet<Opportunity>(`/api/opportunities/${opportunityId}`);
}

export async function getOpportunityIntel(opportunityId: string): Promise<DealIntel> {
  return apiGet<DealIntel>(`/api/opportunities/${opportunityId}/intel`);
}

export async function createOpportunity(input: OpportunityInput): Promise<Opportunity> {
  const res = await apiPost<{ opportunity: Opportunity }>("/api/opportunities", input);
  return res.opportunity;
}

export async function updateOpportunity(
  opportunityId: string,
  updates: Partial<Opportunity>,
): Promise<void> {
  await apiPatch(`/api/opportunities/${opportunityId}`, updates);
}

export async function deleteOpportunity(opportunityId: string): Promise<void> {
  await apiDelete(`/api/opportunities/${opportunityId}`);
}

export async function parseOpportunityUrl(
  opportunityId: string,
  mode: "ai" | "offline" = "ai",
): Promise<{ success: boolean; method?: "ai" | "offline"; description?: string; title?: string; error?: string }> {
  return apiPost(`/api/opportunities/${opportunityId}/parse`, { mode });
}

export interface OpportunityUrlParseFields {
  company?: string;
  dealName?: string;
  location?: string;
  estimatedValue?: string;
  brief?: string;
  tldr?: string;
  workMode?: string;
  payRange?: string;
}

export interface OpportunityUrlParseResult {
  success: boolean;
  method: "ai" | "offline";
  error?: string;
  fields: OpportunityUrlParseFields;
}

export async function parseOpportunityUrlPreview(
  url: string,
  mode: "ai" | "offline" = "ai",
): Promise<OpportunityUrlParseResult> {
  return apiPost<OpportunityUrlParseResult>("/api/opportunities/parse-url", { url, mode });
}

export async function researchOpportunityPreview(input: {
  url?: string;
  company: string;
  dealName?: string;
  brief?: string;
  promptKind?: string;
}): Promise<{ success: boolean; research: string; error?: string }> {
  return apiPost("/api/opportunities/research-preview", input);
}

export async function researchOpportunity(
  opportunityId: string,
  input?: { promptKind?: string },
): Promise<{ success: boolean; research?: string; error?: string }> {
  return apiPost(`/api/opportunities/${opportunityId}/research`, input ?? {});
}
